import { useMemo, useState } from "react";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUiTranslation } from "@tubo/ui/i18n";
import { ArrowLeft } from "lucide-react";
import {
  getGetAssetBatchQueryKey,
  useApplyAssetBatch,
  useCancelAssetBatch,
  useGetAssetBatch,
  useResubmitAssetBatch,
  useReturnAssetBatch,
} from "@tubo/api-models/hooks/asset-ingest-api.hooks";
import { Button } from "@tubo/ui/components/ui/button";
import { Badge } from "@tubo/ui/components/ui/badge";
import { IngestionBatchStatusEnum } from "@tubo/api-models/business/data-asset/data-asset.enums";
import { BATCH_STATUS_KEY } from "./$assetId";
import { ReviewWorkbench } from "@client/components/semantic-platform/data-assets/batches/review-workbench";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@tubo/ui/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import { Textarea } from "@tubo/ui/components/ui/textarea";

/** 可取消状态(附录 A:receiving/preAnalyzed/reviewing → cancelled) */
const cancelableStatuses: readonly string[] = [
  IngestionBatchStatusEnum.Receiving,
  IngestionBatchStatusEnum.PreAnalyzed,
  IngestionBatchStatusEnum.Reviewing,
];

export const Route = createFileRoute("/_app/semantic-platform/data-assets/batches/$batchId")({
  component: AssetBatchDetailPage,
});

/**
 * 批次详情(波次4 4A-5):计数/拦截项/包引用(sourceRunId + generation + checksum);
 * 操作后 invalidateQueries 刷新(工程规范硬约束)。
 */
function AssetBatchDetailPage() {
  const { t } = useUiTranslation("semanticPlatformDetails");
  const { batchId } = Route.useParams();
  const navigate = useNavigate();
  const { searchStr } = useLocation();
  const listSearch = new URLSearchParams(searchStr).get("ns");
  const queryClient = useQueryClient();
  const { data: batch, isLoading } = useGetAssetBatch(batchId, { query: { refetchInterval: 10_000 } });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: getGetAssetBatchQueryKey(batchId) });
  };

  const { mutate: applyMutate, isPending: applying } = useApplyAssetBatch({ mutation: { onSuccess: async () => { toast.success(t("assetBatch.applied")); await invalidate(); }, onError: (e) => toast.error(e instanceof Error ? e.message : t("assetBatch.applyFailed")) } });
  const { mutate: returnMutate, isPending: returning } = useReturnAssetBatch({ mutation: { onSuccess: async () => { toast.success(t("assetBatch.returned")); await invalidate(); }, onError: (e) => toast.error(e instanceof Error ? e.message : t("assetBatch.returnFailed")) } });
  const { mutate: cancelMutate, isPending: canceling } = useCancelAssetBatch({ mutation: { onSuccess: async () => { toast.success(t("assetBatch.cancelled")); await invalidate(); }, onError: (e) => toast.error(e instanceof Error ? e.message : t("assetBatch.cancelFailed")) } });
  const { mutate: resubmitMutate, isPending: resubmitting } = useResubmitAssetBatch({ mutation: { onSuccess: async (resp) => { toast.success(t("assetBatch.resubmitted")); await invalidate(); navigate({ to: "/semantic-platform/data-assets/batches/$batchId", params: { batchId: resp.batchId }, search: listSearch ? { ns: listSearch } : {} }); }, onError: (e) => toast.error(e instanceof Error ? e.message : t("assetBatch.resubmitFailed")) } });

  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  // 头部注入域级 SecondaryNav(标题区:返回/批次/状态;操作区:整批接受/退回/重新提交/取消),页面体不再自写标题栏
  const headerTitle = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t("assetBatch.backToAsset")}
          disabled={!batch}
          onClick={() => batch && navigate({ to: "/semantic-platform/data-assets/$assetId", params: { assetId: batch.assetId }, search: listSearch ? { ns: listSearch } : {} })}
        >
          <ArrowLeft className="size-4" />
        </Button>
        {batch ? (
          <>
            <span className="text-sm font-semibold">{t("assetBatch.title", { id: batch.id.slice(-8) })}</span>
            <Badge variant="secondary">{BATCH_STATUS_KEY[batch.status] ? t(`assetDetail.batchStatus.${BATCH_STATUS_KEY[batch.status]}`) : batch.status}</Badge>
          </>
        ) : (
          <span className="text-sm font-semibold">{t("assetBatch.moduleName")}</span>
        )}
      </div>
    ),
    [batch, listSearch, navigate, t],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () =>
      batch ? (
        <div className="flex items-center gap-2">
          {batch.status === IngestionBatchStatusEnum.Reviewing && (
            <>
              <Button size="sm" onClick={() => applyMutate({ id: batch.id })} disabled={applying}>{t("assetBatch.acceptAll")}</Button>
              <Button variant="outline" size="sm" onClick={() => { setReturnReason(""); setReturnOpen(true); }} disabled={returning}>{t("assetBatch.return")}</Button>
            </>
          )}
          {batch.status === IngestionBatchStatusEnum.Returned && (
            <Button size="sm" variant="outline" onClick={() => resubmitMutate({ id: batch.id })} disabled={resubmitting}>{t("assetBatch.resubmit")}</Button>
          )}
          {cancelableStatuses.includes(batch.status) && (
            <Button variant="outline" size="sm" onClick={() => setCancelConfirmOpen(true)} disabled={canceling}>{t("assetBatch.cancel")}</Button>
          )}
        </div>
      ) : null,
    [batch, applyMutate, applying, returning, resubmitMutate, resubmitting, canceling, t],
  );
  useSecondaryNavActions(headerActions);

  if (isLoading || !batch) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">{t("assetBatch.loading")}</div>;
  }
  const blockers = batch.blockers ?? [];

  return (
    <div className="flex h-full flex-col">
      <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("assetBatch.returnTitle")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-1.5">
            <span className="text-sm text-muted-foreground">{t("assetBatch.returnReason")}</span>
            <Textarea rows={3} value={returnReason} onChange={(e) => setReturnReason(e.target.value)} placeholder={t("assetBatch.returnReasonPlaceholder")} />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setReturnOpen(false)}>{t("assetBatch.cancel")}</Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={returning}
              onClick={() => {
                returnMutate({ id: batch.id, data: { ...(returnReason.trim() ? { reason: returnReason.trim() } : {}) } });
                setReturnOpen(false);
              }}
            >
              {t("assetBatch.confirmReturn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("assetBatch.cancelTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("assetBatch.cancelDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("assetBatch.reconsider")}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => cancelMutate({ id: batch.id })}
            >
              {t("assetBatch.confirmCancel")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex-1 overflow-auto px-4 py-3">
        <div className="grid gap-2 text-sm">
          <div className="flex gap-2"><span className="w-28 text-muted-foreground">{t("assetBatch.sourceRun")}</span><span className="font-mono text-xs">{batch.sourceRunId}</span></div>
          <div className="flex gap-2"><span className="w-28 text-muted-foreground">{t("assetBatch.generation")}</span><span>{batch.generation ?? "-"}</span></div>
          <div className="flex gap-2"><span className="w-28 text-muted-foreground">{t("assetBatch.checksum")}</span><span className="font-mono text-xs">{batch.packageChecksum.slice(0, 24)}…</span></div>
          <div className="flex gap-2"><span className="w-28 text-muted-foreground">{t("assetBatch.modelVersion")}</span><span>{batch.modelVersion}</span></div>
          <div className="flex gap-2"><span className="w-28 text-muted-foreground">{t("assetBatch.counts")}</span><span>{t("assetBatch.newCount", { count: batch.newCount ?? 0 })} / {t("assetBatch.updateCount", { count: batch.updateCount ?? 0 })} / {t("assetBatch.conflictCount", { count: batch.conflictCount ?? 0 })} / {t("assetBatch.duplicateCount", { count: batch.duplicateCount ?? 0 })}</span></div>
          {batch.returnReason && <div className="flex gap-2"><span className="w-28 text-muted-foreground">{t("assetBatch.returnReasonLabel")}</span><span>{batch.returnReason}</span></div>}
        </div>

        {blockers.length > 0 && (
          <div className="mt-4">
            <h2 className="mb-2 text-sm font-medium">{t("assetBatch.blockers", { count: blockers.length })}</h2>
            <div className="grid gap-2">
              {blockers.map((b, i) => (
                <div key={i} className="rounded-sm border px-3 py-2 text-xs">
                  <Badge variant="destructive" className="mr-2">{b.code}</Badge>
                  {b.message}
                  <div className="mt-1 font-mono text-muted-foreground">{b.targetJson}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {batch.status === IngestionBatchStatusEnum.Reviewing && (
          <div className="mt-4">
            <ReviewWorkbench batchId={batch.id} />
          </div>
        )}
      </div>
    </div>
  );
}
